import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import DxDataTable from 'components/DxDataTable'
import 'devextreme/dist/css/dx.light.css'

import { ReactComponent as Back } from 'media/icons/rc_arrow_back.svg'
import { ReactComponent as Home } from 'media/icons/rc_circle_outline.svg'
import { ReactComponent as Stack } from 'media/icons/rc_box_outline.svg'
import { ReactComponent as Power } from 'media/icons/rc_power.svg'
import { ReactComponent as Restart } from 'media/icons/rc_restart.svg'
import { ReactComponent as Sidebar } from 'media/icons/rc_sidebar.svg'
import { ReactComponent as Display } from 'media/icons/rc_display.svg'
import { ReactComponent as ExitKiosk } from 'media/icons/rc_exitkisok.svg'
import { ReactComponent as RequestPermissions } from 'media/icons/rc_requestpermissions.svg'

import RemoteControlButton from './RemoteControlButton'
import RemoteControlVideo from './RemoteControlVideo'
import FileManager from './FileManager'
import { StyledMenuBar, StreamContainer, StyledMenuSubgroup, StreamLoader, StreamRestarter, StatusText } from './styled'
import useStreamViewer from './hooks/useStreamViewer'

const RemoteAccess = ({ data, appData, queryResult, resetPage, appManagerVersion, isDeviceOnline, ...rest }) => {
	const videoRef = useRef()
	const [isSideDisplayOn, setSideDisplayState] = useState(false)
	const [statusText, setStatusText] = useState('')
	const [logsArray, setLogsArray] = useState([])
	const statusTextTimer = useRef(null)
	const [t] = useTranslation()

	const handleSetStatusText = text => {
		if(statusTextTimer.current) {
			clearTimeout(statusTextTimer.current)
		}
		statusTextTimer.current = setTimeout(() => {
			setStatusText(null)
			statusTextTimer.current = null
		}, 5 * 1000)
		setStatusText(text)
	}

	const res = useStreamViewer({
		videoRef,
		hardwareId: data?.hardwareId,
		clientId: appData.authData.attributes.sub,
		appManagerVersion,
		onMessage: (message) => {
			if(message.PROGRESS) {
				handleSetStatusText(`[DOWNLOAD] ${message.PROGRESS}%`)
			}
			if(message.type === 'LOG') {
				setLogsArray(prev => [...prev, message])
			}
		},
		onError: () => handleSetStatusText(`[ERROR] ${JSON.stringify(hasError)}`),
		onDeviceStateChange: (status) => handleSetStatusText(`[DEVICE] status: ${JSON.stringify(status)}`)
	})
	const {
		isLoading,
		hasClosed,
		hasError,
		sendCommandMessage,
		sendRequest,
		isDataChannelReady
	} = res

	const handleSideDisplayButton = async () => {
		const command = isSideDisplayOn ? 'PRESENTATION_OFF' : 'PRESENTATION_ON'
		const result = await sendRequest({ command })
		if(result[command] === 'SUCCESS') {
			setSideDisplayState(command === 'PRESENTATION_ON' ? true : false)
		}
	}

	return(
		<ExpandableBlocks>
			{!isDeviceOnline &&
				<ContentBlock alwaysExpanded showHeader={false}>{t('options_section.strings.device_may_be_offline')}</ContentBlock>
			}
			<StreamContainer>
				{isLoading &&
					<StreamLoader isLoading/>
				}
				{(hasClosed || hasError) &&
					<StreamRestarter >
						<div onClick={resetPage}>
							<Restart/>
							Restart stream
						</div>
					</StreamRestarter>
				}
				<RemoteControlVideo
					id='streamPlayerId'
					handleClick={coordinates => sendCommandMessage({ command: 'SINGLE_TAP', coordinates })}
					handleDoubleClick={coordinates => sendCommandMessage({ command: 'DOUBLE_TAP', coordinates })}
					handleLongPress={coordinates => sendCommandMessage({ command: 'LONG_TAP', coordinates })}
					handleSwipe={coordArr => sendCommandMessage({ command: 'SWIPE', coordinatesStart: coordArr[0], coordinatesEnd: coordArr[1] })}
					videoRef={videoRef}
					muted
					type='video/mp4'
					autoPlay
					loop
					playsInline
				/>
				{!!statusText && <StatusText>{statusText}</StatusText>}
				<StyledMenuBar>
					<StyledMenuSubgroup width='32px'>
						<RemoteControlButton tooltip={t('options_section.strings.open_sidebar')} onClick={() => sendCommandMessage({ command: 'SIDEBAR' })} onLongClick={() => sendCommandMessage({ command: 'TAKE_LOCAL_DB_SNAPSHOT' })}><Sidebar/></RemoteControlButton>
						<RemoteControlButton tooltip={t('options_section.strings.secondary_display')} onClick={handleSideDisplayButton}><Display/></RemoteControlButton>
						<RemoteControlButton tooltip={t('options_section.strings.exit_kiosk')} onClick={() => sendCommandMessage({ command: 'EXIT_KIOSK' })}><ExitKiosk/></RemoteControlButton>
						<RemoteControlButton tooltip={t('options_section.strings.request_permissions')} onClick={() => sendCommandMessage({ command: 'REQUEST_SCREEN_CAPTURE_PERMISSIONS' })}><RequestPermissions/></RemoteControlButton>
						{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'COPY' })}><Copy/></RemoteControlButton> */}
						{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'PASTE' })}><Paste/></RemoteControlButton> */}
					</StyledMenuSubgroup>
					{/* <StyledMenuSubgroup width='96px'> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'COPY' })}><Copy/></RemoteControlButton> */}
					{/* <RemoteControlButton onClick={() => sendCommandMessage({ command: 'PASTE' })}><Paste/></RemoteControlButton> */}
					{/* </StyledMenuSubgroup> */}
					<StyledMenuSubgroup width='256px'>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'BACK' })}><Back/></RemoteControlButton>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'HOME' })}><Home/></RemoteControlButton>
						<RemoteControlButton onClick={() => sendCommandMessage({ command: 'STACK' })}><Stack/></RemoteControlButton>
					</StyledMenuSubgroup>

					<RemoteControlButton style={{ marginLeft: '64px' }} onClick={() => sendCommandMessage({ command: 'POWER' })}><Power/></RemoteControlButton>
				</StyledMenuBar>
			</StreamContainer>
			<ContentBlock label={t('options_section.strings.file_system')}>
				{isDataChannelReady && <FileManager sendRequest={sendRequest}/>}
			</ContentBlock>
			<ContentBlock label={t('options_section.strings.log_file')}>
				<DxDataTable
					height={500}
					withSearch={false}
					columns={[{
						caption: t('common.strings.actions'),
						dataField: 'action'
					}, {
						caption: t('common.strings.target'),
						dataField: 'target'
					}, {
						caption: t('common.strings.timestamp'),
						dataField: 'time',
						dataType: 'datetime',
						sortIndex: 1,
						sortOrder: 'desc'
					}]}
					data={logsArray}
				/>
			</ContentBlock>
		</ExpandableBlocks>
	)
}

export default RemoteAccess
